import React, { useState } from 'react';
import styled from 'styled-components';
import { AlertCircle, Check, Info, Lock, Plus, Trash2 } from 'lucide-react';
import Fieldset from 'components/_UI/Fieldset';
import CurrencyInputField from 'components/_UI/CurrencyInputField';
import Switch from 'components/_UI/Switch';
import currency from 'helpers/currency';
import useEventServiceFee from 'hooks/useEventServiceFee';
import { SaleTypes } from 'modules/_common/SaleTypes';
import SalePeriodForm from './SalePeriodForm';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import {
    BATCH_NAME_MAX,
    MIN_PRICE,
    currentLotIndex,
    isLotSoldOut,
    isTicketFree,
    lotIncome,
    lotsOf,
    newLot,
    patchLot,
    setTicketFree,
    autoLotName,
    lotName,
} from './ticketModel';
import {
    Chip,
    DashedButton,
    FieldError,
    FieldHint,
    INK,
    INK_SOFT,
    IconButton,
    LINE,
    MUTED,
    SectionTitle,
    TextButton,
    accent,
    accentAlpha,
} from './ticketFormStyles';

function priceText(value) {
    return currency.format(value).replace('R$ ', '');
}

/**
 * Preço e lotes do ingresso, numa linha do tempo.
 *
 * Todos os lotes têm a mesma cara — o primeiro inclusive, que no estado mora nos
 * campos do ingresso (ver `lotsOf`). O conector entre eles conta a regra que o
 * produtor não enxergava: no modo por lote, o seguinte só abre quando o
 * anterior esgota.
 */
export default function BatchTimeline({
    ticket,
    onChange,
    isEventFree,
    absorbed,
    errorFor,
    touch,
    onDeleteBatchId,
}) {
    const { ready: feeReady, calculateFee, calculateFeePix, calculatePriceFromNet } = useEventServiceFee();
    const [deleteIndex, setDeleteIndex] = useState(null);
    const [reverse, setReverse] = useState(false);
    const [reverseValues, setReverseValues] = useState({});

    const free = isTicketFree(ticket, isEventFree);
    const isPeriod = ticket.sale_type === SaleTypes.PERIOD;
    const lots = lotsOf(ticket);
    const current = currentLotIndex(ticket);
    const canReverse = absorbed && !free;

    function updateLot(index, patch) {
        onChange(previous => patchLot(previous, index, patch));
    }

    // Ingresso gratuito é decisão do produtor, não do tipo do evento.
    function toggleFree(value) {
        setReverse(false);
        onChange(previous => setTicketFree(previous, value));
    }

    function changePrice(index, value, values) {
        updateLot(index, { price: value, floatPrice: values?.float ?? 0 });
        touch('price', index);
    }

    function changeReverse(index, value, values) {
        const desiredNet = values?.float ?? 0;
        const price = desiredNet > 0 ? calculatePriceFromNet(desiredNet) : 0;

        setReverseValues(previous => ({ ...previous, [lots[index].key]: value }));
        updateLot(index, { price: price ? priceText(price) : '', floatPrice: price });
        touch('price', index);
    }

    function enterReverse() {
        const values = {};

        lots.forEach(lot => {
            const { net } = lotIncome(lot, { free, absorbed, calculateFee, calculateFeePix });
            values[lot.key] = net > 0 ? priceText(net) : '';
        });

        setReverseValues(values);
        setReverse(true);
    }

    function addLot() {
        onChange(previous => ({
            ...previous,
            batches: [...previous.batches, newLot(free)],
        }));
    }

    function confirmDelete() {
        const lot = lots[deleteIndex];

        if (lot?.id) onDeleteBatchId(lot.id);

        onChange(previous => ({
            ...previous,
            batches: previous.batches.filter((_, i) => i !== deleteIndex - 1),
        }));
        setDeleteIndex(null);
    }

    function lotStatus(lot, index) {
        if (isLotSoldOut(lot)) return { label: 'Esgotado' };
        if (index === current) return { label: 'À venda agora', selling: true };
        if (!lot.id) return ticket.id ? { label: 'Novo' } : null;
        return { label: isPeriod ? 'No período' : 'Na fila' };
    }

    function connectorText(index) {
        if (isPeriod) return null;

        const previous = lots[index - 1];

        if (isLotSoldOut(previous)) return `${lotName(ticket, previous, index - 1)} esgotou — este lote abriu automaticamente`;

        return `Abre quando o ${lotName(ticket, previous, index - 1)} esgotar`;
    }

    function incomeLabel(lot) {
        if (free) return { main: 'Gratuito' };

        const price = Number(lot.floatPrice) || 0;

        if (price <= 0) return { main: '—' };

        // Sem a taxa do evento em mãos não há número honesto a mostrar: o
        // produtor precifica em cima desta linha.
        if (absorbed && !feeReady) return { main: '—' };

        const { net, netPix } = lotIncome(lot, { free, absorbed, calculateFee, calculateFeePix });

        if (!absorbed) return { main: currency.format(net) };

        return { main: currency.format(net), sub: `${currency.format(netPix)} no Pix`, mainSuffix: 'no cartão' };
    }

    return (
        <Section aria-labelledby={`lots-${ticket.internal_id}`}>
            <SectionHeader>
                <SectionTitle id={`lots-${ticket.internal_id}`}>Preço e lotes</SectionTitle>
                {!isEventFree && (
                    <SwitchLine>
                        <Switch id={`free-${ticket.internal_id}`} checked={free} onChange={toggleFree} />
                        <label htmlFor={`free-${ticket.internal_id}`}>Ingresso gratuito</label>
                    </SwitchLine>
                )}
            </SectionHeader>

            {free && (
                <FieldHint>
                    {isEventFree
                        ? 'Evento gratuito: todos os ingressos saem a R$ 0,00, sem taxa de serviço.'
                        : 'O comprador não paga nada e não há taxa de serviço — bom para cortesia, convite ou lista.'}
                </FieldHint>
            )}

            <Lots>
                {lots.map((lot, index) => {
                    const status = lotStatus(lot, index);
                    const soldOut = isLotSoldOut(lot);
                    const income = incomeLabel(lot);
                    const connector = index > 0 ? connectorText(index) : null;
                    const priceChanged = lot.committed > 0
                        && lot.originalPrice !== undefined
                        && Math.abs((Number(lot.floatPrice) || 0) - lot.originalPrice) > 0.004;
                    const priceError = errorFor('price', index);
                    const quantityError = errorFor('quantity', index);
                    const nameError = errorFor('lotName', index);
                    const periodError = errorFor('period', index);
                    const lowPrice = !free && lot.floatPrice > 0 && lot.floatPrice < MIN_PRICE;
                    const idPrefix = `${lot.key}`;

                    return (
                        <React.Fragment key={lot.key}>
                            {index > 0 && (
                                <Connector aria-hidden='true'>
                                    <Rail dashed={!isLotSoldOut(lots[index - 1])} />
                                    {connector && <ConnectorText>{connector}</ConnectorText>}
                                </Connector>
                            )}

                            <LotRow>
                                <Marker
                                    variant={soldOut ? 'done' : index === current ? 'current' : lot.id ? 'queued' : 'new'}
                                    aria-hidden='true'
                                >
                                    {soldOut ? <Check size={16} strokeWidth={2.6} /> : index + 1}
                                </Marker>

                                <LotCard soldOut={soldOut} current={index === current}>
                                    <LotHead>
                                        <LotTitle soldOut={soldOut}>{lotName(ticket, lot, index)}</LotTitle>
                                        <LotHeadRight>
                                            {status && (
                                                status.selling
                                                    ? <Chip bg='#F7EFFF' fg='#6D14CC' border='#DFC8F8'>{status.label}</Chip>
                                                    : <Chip>{status.label}</Chip>
                                            )}
                                            {index > 0 && lot.committed === 0 && (
                                                <IconButton
                                                    type='button'
                                                    danger
                                                    aria-label={`Excluir ${lotName(ticket, lot, index)}`}
                                                    onClick={() => setDeleteIndex(index)}
                                                    style={{ width: 36, height: 36, borderColor: 'transparent' }}
                                                >
                                                    <Trash2 size={16} aria-hidden='true' />
                                                </IconButton>
                                            )}
                                        </LotHeadRight>
                                    </LotHead>

                                    <LotGrid>
                                        <Cell data-invalid={nameError ? 'true' : undefined}>
                                            <Fieldset
                                                id={`${idPrefix}-name`}
                                                layout='squired'
                                                label='Nome do lote'
                                                placeholder={autoLotName(ticket, index)}
                                                value={lot.name || ''}
                                                syncProp
                                                maxLength={BATCH_NAME_MAX}
                                                onChangeValue={value => {
                                                    updateLot(index, { name: value });
                                                    touch('lotName', index);
                                                }}
                                            />
                                            {nameError && <FieldError><AlertCircle size={12} aria-hidden='true' />{nameError}</FieldError>}
                                        </Cell>

                                        <Cell data-invalid={priceError ? 'true' : undefined}>
                                            {reverse && canReverse ? (
                                                <CurrencyInputField
                                                    id={`${idPrefix}-net`}
                                                    layout='squired'
                                                    label='Quero receber'
                                                    placeholder='R$ 45,00'
                                                    value={reverseValues[lot.key] ?? ''}
                                                    onChange={(value, name, values) => changeReverse(index, value, values)}
                                                />
                                            ) : (
                                                <CurrencyInputField
                                                    id={`${idPrefix}-price`}
                                                    layout='squired'
                                                    label='Valor'
                                                    placeholder='R$ 0,00'
                                                    value={free ? '0,00' : lot.price}
                                                    disabled={free}
                                                    onChange={(value, name, values) => changePrice(index, value, values)}
                                                />
                                            )}
                                            {priceError && <FieldError><AlertCircle size={12} aria-hidden='true' />{priceError}</FieldError>}
                                            {!priceError && lowPrice && (
                                                <FieldError><AlertCircle size={12} aria-hidden='true' />Mínimo de R$ 5,00. Para R$ 0,00, ative “Ingresso gratuito”.</FieldError>
                                            )}
                                        </Cell>

                                        <Cell data-invalid={quantityError ? 'true' : undefined}>
                                            <Fieldset
                                                id={`${idPrefix}-quantity`}
                                                layout='squired'
                                                label='Quantidade'
                                                placeholder='100'
                                                value={lot.quantity ?? ''}
                                                syncProp
                                                onChangeValue={value => {
                                                    updateLot(index, { quantity: value ? value.replace(/\D/g, '') : '' });
                                                    touch('quantity', index);
                                                }}
                                            />
                                            {quantityError && <FieldError><AlertCircle size={12} aria-hidden='true' />{quantityError}</FieldError>}
                                        </Cell>

                                        <Cell>
                                            <ValueLabel>
                                                {reverse && canReverse ? 'Comprador paga' : 'Você recebe'}
                                            </ValueLabel>
                                            {reverse && canReverse ? (
                                                <Value strong={lot.floatPrice > 0}>{lot.floatPrice > 0 ? currency.format(lot.floatPrice) : '—'}</Value>
                                            ) : (
                                                <Value strong={income.main !== '—'}>
                                                    {income.main}
                                                    {income.sub && <ValueSub>{income.mainSuffix} · {income.sub}</ValueSub>}
                                                </Value>
                                            )}
                                        </Cell>
                                    </LotGrid>

                                    {lot.committed > 0 && (
                                        <Sold title='Inclui reservas de pedidos ainda não pagos'>
                                            <SoldBar>
                                                <span style={{ width: `${Math.min((lot.committed / (Number(lot.quantity) || 1)) * 100, 100)}%` }} />
                                            </SoldBar>
                                            <SoldText>{lot.committed} de {Number(lot.quantity) || 0} vendidos</SoldText>
                                        </Sold>
                                    )}

                                    {priceChanged && (
                                        <Notice role='status'>
                                            <Info size={16} aria-hidden='true' />
                                            <p>
                                                Preço alterado de <strong>{currency.format(lot.originalPrice)}</strong> para{' '}
                                                <strong>{currency.format(lot.floatPrice || 0)}</strong>. Vale só para as
                                                próximas vendas: os {lot.committed} já vendidos mantêm o valor antigo.
                                            </p>
                                        </Notice>
                                    )}

                                    {isPeriod && (
                                        <Period data-invalid={periodError ? 'true' : undefined}>
                                            <PeriodLabel>Período de vendas deste lote</PeriodLabel>
                                            <SalePeriodForm
                                                salePeriod={{ sale_start: lot.sale_start, sale_end: lot.sale_end }}
                                                onChangeSalePeriod={({ sale_start, sale_end }) => {
                                                    updateLot(index, { sale_start, sale_end });
                                                    touch('period', index);
                                                }}
                                            />
                                            {periodError && <FieldError><AlertCircle size={12} aria-hidden='true' />{periodError}</FieldError>}
                                        </Period>
                                    )}

                                    {(index > 0 || lot.committed > 0) && (
                                        <LotFooter>
                                            {index > 0 && (
                                                <SwitchLine small>
                                                    <Switch
                                                        id={`${idPrefix}-active`}
                                                        checked={!!lot.is_active}
                                                        onChange={value => updateLot(index, { is_active: value })}
                                                    />
                                                    <label htmlFor={`${idPrefix}-active`}>Lote ativo</label>
                                                </SwitchLine>
                                            )}
                                            {lot.committed > 0 && (
                                                <LockNote>
                                                    <Lock size={13} aria-hidden='true' />
                                                    {index === 0
                                                        ? 'Lote com vendas: a quantidade não pode ficar abaixo do vendido.'
                                                        : 'Lote com vendas: não pode ser excluído nem ficar abaixo do vendido.'}
                                                </LockNote>
                                            )}
                                        </LotFooter>
                                    )}
                                </LotCard>
                            </LotRow>
                        </React.Fragment>
                    );
                })}

                {!isEventFree && (
                    <AddLotRow>
                        <MarkerSpacer />
                        <DashedButton type='button' onClick={addLot}>
                            <Plus size={18} aria-hidden='true' />
                            <AddText>
                                <strong>Adicionar lote</strong>
                                <span>
                                    {isPeriod
                                        ? 'Cada lote vende no período que você definir.'
                                        : 'Os lotes viram sozinhos: quando um esgota, o próximo abre.'}
                                </span>
                            </AddText>
                        </DashedButton>
                    </AddLotRow>
                )}
            </Lots>

            {canReverse && (
                <TextButton type='button' onClick={() => (reverse ? setReverse(false) : enterReverse())}>
                    {reverse ? 'Voltar para o preço de venda' : 'Prefiro definir quanto quero receber'}
                </TextButton>
            )}

            <ConfirmDeleteModal
                isOpen={deleteIndex !== null}
                title='Excluir lote'
                message='Tem certeza que deseja excluir este lote? Esta ação não pode ser desfeita.'
                onConfirm={confirmDelete}
                onCancel={() => setDeleteIndex(null)}
            />
        </Section>
    );
}

const Section = styled.section`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

const SwitchLine = styled.div.withConfig({
    shouldForwardProp: prop => prop !== 'small',
})`
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: ${({ small }) => (small ? '36px' : '44px')};

    label {
        font-size: ${({ small }) => (small ? '13px' : '14px')};
        color: ${INK};
        cursor: pointer;
    }

    /* O Switch compartilhado traz margem própria; aqui o gap já espaça. */
    & > div {
        width: auto;
        flex: 0 0 auto;
        margin: 0;
    }
`;

const Lots = styled.ol`
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
`;

const LotRow = styled.li`
    display: flex;
    gap: 14px;
`;

const AddLotRow = styled(LotRow)`
    margin-top: 16px;
`;

const Marker = styled.span.withConfig({
    shouldForwardProp: prop => prop !== 'variant',
})`
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    margin-top: 12px;
    border-radius: 50%;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;

    ${({ variant }) => {
        if (variant === 'done') return `background: #E4E4EA; color: ${INK_SOFT};`;
        if (variant === 'queued') return `background: #fff; border: 2px solid #B9B9C2; color: ${INK_SOFT};`;
        if (variant === 'current') return `background: ${accent}; color: #fff; box-shadow: 0 0 0 4px ${accentAlpha('26')};`;
        return `background: ${accent}; color: #fff;`;
    }}
`;

const MarkerSpacer = styled.span`
    width: 32px;
    flex-shrink: 0;
`;

const Connector = styled.li`
    display: flex;
    gap: 14px;
    min-height: 28px;
`;

const Rail = styled.span.withConfig({
    shouldForwardProp: prop => prop !== 'dashed',
})`
    width: 32px;
    flex-shrink: 0;
    display: flex;
    justify-content: center;

    &::before {
        content: '';
        width: 2px;
        background: ${({ dashed }) => (dashed
            ? 'repeating-linear-gradient(#D9D9E0 0 4px, transparent 4px 8px)'
            : '#D9D9E0')};
    }
`;

const ConnectorText = styled.span`
    align-self: center;
    padding: 6px 0;
    font-size: 12px;
    color: ${MUTED};
`;

const LotCard = styled.div.withConfig({
    shouldForwardProp: prop => !['soldOut', 'current'].includes(prop),
})`
    flex: 1 1 auto;
    min-width: 0;
    border: ${({ current }) => (current ? `2px solid ${accent}` : `1px solid ${LINE}`)};
    background: ${({ soldOut }) => (soldOut ? '#FAFAFB' : '#fff')};
    border-radius: 12px;
    padding: 12px 14px 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const LotHead = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 36px;
`;

const LotTitle = styled.span.withConfig({
    shouldForwardProp: prop => prop !== 'soldOut',
})`
    font-size: 15px;
    font-weight: 600;
    color: ${({ soldOut }) => (soldOut ? INK_SOFT : INK)};
`;

const LotHeadRight = styled.span`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const LotGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    align-items: start;

    @media screen and (min-width: 1100px) {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }
`;

const Cell = styled.div`
    min-width: 0;
`;

const ValueLabel = styled.span`
    display: block;
    font-size: 14px;
    font-weight: 700;
    color: ${INK};
    margin-bottom: 8px;
`;

const Value = styled.span.withConfig({
    shouldForwardProp: prop => prop !== 'strong',
})`
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 44px;
    font-size: 16px;
    font-weight: ${({ strong }) => (strong ? 700 : 500)};
    color: ${({ strong }) => (strong ? INK : '#8A8A93')};
`;

const ValueSub = styled.span`
    font-size: 12px;
    font-weight: 400;
    color: ${MUTED};
`;

const Sold = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const SoldBar = styled.span`
    flex: 1;
    height: 6px;
    border-radius: 99px;
    background: #ECECEC;
    overflow: hidden;

    span {
        display: block;
        height: 100%;
        background: #9A9AA3;
    }
`;

const SoldText = styled.span`
    font-size: 13px;
    color: ${INK_SOFT};
    white-space: nowrap;
`;

const Notice = styled.div`
    display: flex;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    background: #FFF8EC;
    border: 1px solid #F3DFAE;
    color: #5C3A00;

    svg {
        flex-shrink: 0;
        margin-top: 1px;
        color: #8A5300;
    }

    p {
        margin: 0;
        font-size: 13px;
        line-height: 1.45;
    }
`;

const Period = styled.div`
    border-top: 1px solid #F0F0F3;
    padding-top: 10px;
`;

const PeriodLabel = styled.p`
    margin: 0 0 4px;
    font-size: 13px;
    font-weight: 600;
    color: ${INK};
`;

const LotFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 6px 16px;
`;

const LockNote = styled.p`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-size: 12px;
    color: ${MUTED};
`;

const AddText = styled.span`
    display: flex;
    flex-direction: column;
    gap: 2px;

    strong {
        font-size: 14px;
        font-weight: 500;
        color: ${accent};
    }

    span {
        font-size: 12px;
        color: ${MUTED};
    }
`;

